import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, Mail, ShieldCheck } from "lucide-react";
import { TurnstileWidget } from "./TurnstileWidget";
import { configuredApiOrigin, recordMarketingEvent } from "./marketingEvents";

interface BetaConfig {
  available: boolean;
  turnstileSiteKey: string;
  turnstileAction: string;
  consentVersion: string;
}

const fallbackRecipient = "michael@threesistersimprovements.com";

function stringValue(form: FormData, name: string): string {
  return String(form.get(name) ?? "").trim();
}

function fallbackEmail(form: FormData): string {
  const body = [
    "Hi Michael,",
    "",
    "I'd like to apply for the Scopevora private beta.",
    "",
    `Name: ${stringValue(form, "fullName")}`,
    `Email: ${stringValue(form, "email")}`,
    `Phone: ${stringValue(form, "phone") || "Not provided"}`,
    `Company: ${stringValue(form, "companyName")}`,
    `Trade: ${stringValue(form, "trade")}`,
    `Team size: ${stringValue(form, "teamSize")}`,
    `Current software: ${stringValue(form, "currentSoftware") || "Not provided"}`,
    `Main challenge: ${stringValue(form, "primaryChallenge")}`,
    `How I heard about Scopevora: ${stringValue(form, "referralSource") || "Not provided"}`,
  ].join("\n");
  return `mailto:${fallbackRecipient}?subject=${encodeURIComponent("Scopevora private beta application")}&body=${encodeURIComponent(body)}`;
}

export function BetaApplicationForm() {
  const [config, setConfig] = useState<BetaConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [success, setSuccess] = useState(false);
  const submissionId = useRef(`bts_${crypto.randomUUID()}`);
  const formElementRef = useRef<HTMLFormElement>(null);
  const apiEnabled = Boolean(configuredApiOrigin);
  const onToken = useCallback((value: string) => setToken(value), []);
  const onWidgetError = useCallback((message: string) => setNotice(message), []);

  useEffect(() => {
    if (!apiEnabled) return;
    const controller = new AbortController();
    void fetch(`${configuredApiOrigin}/v1/public/beta/config`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("The secure beta form is temporarily unavailable.");
        return response.json() as Promise<BetaConfig>;
      })
      .then((value) => { setConfig(value); setConfigError(""); })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setConfigError(error instanceof Error ? error.message : "The secure beta form is temporarily unavailable.");
      });
    return () => controller.abort();
  }, [apiEnabled]);

  useEffect(() => {
    const element = formElementRef.current;
    if (!element || !apiEnabled || typeof IntersectionObserver === "undefined") return;
    let recorded = false;
    const observer = new IntersectionObserver((entries) => {
      if (!recorded && entries.some((entry) => entry.isIntersecting)) {
        recorded = true;
        recordMarketingEvent("beta_form_view");
        observer.disconnect();
      }
    }, { threshold: .25 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [apiEnabled]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setNotice("");
    setSuccess(false);

    if (!apiEnabled) {
      window.location.href = fallbackEmail(form);
      setNotice("Your email app should open with the application filled in. Review it and press Send to deliver it.");
      return;
    }
    if (!config?.available) {
      setNotice(configError || "The secure beta form is temporarily unavailable. Please try again shortly.");
      return;
    }

    setBusy(true);
    try {
      const response = await fetch(`${configuredApiOrigin}/v1/public/beta/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submissionId: submissionId.current,
          fullName: stringValue(form, "fullName"),
          email: stringValue(form, "email"),
          companyName: stringValue(form, "companyName"),
          phone: stringValue(form, "phone"),
          trade: stringValue(form, "trade"),
          teamSize: stringValue(form, "teamSize"),
          currentSoftware: stringValue(form, "currentSoftware"),
          primaryChallenge: stringValue(form, "primaryChallenge"),
          referralSource: stringValue(form, "referralSource"),
          sourcePage: window.location.href,
          consent: form.get("consent") === "on",
          consentVersion: config.consentVersion,
          turnstileToken: token,
        }),
      });
      const payload = await response.json().catch(() => ({})) as { message?: string; error?: { message?: string } };
      if (!response.ok) throw new Error(payload.error?.message ?? payload.message ?? "The application could not be submitted.");
      setSuccess(true);
      setNotice("Application received. We’ll review your workflow and follow up by email—no card or subscription was created.");
      formElement.reset();
      submissionId.current = `bts_${crypto.randomUUID()}`;
      setResetSignal((value) => value + 1);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "The application could not be submitted.");
      setResetSignal((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }

  const secureFormLoading = apiEnabled && !config && !configError;
  const requiresChallenge = Boolean(config?.turnstileSiteKey);
  return <form className="beta-form" ref={formElementRef} onSubmit={submit}>
    <div className="beta-form-heading">
      <span><ShieldCheck /></span>
      <div><p className="eyebrow">Private beta application</p><h3>Tell us about your company.</h3><p>We’re inviting a small group of contractors whose real workflows can shape the first release.</p></div>
    </div>
    <div className="beta-form-grid">
      <label>Full name<input name="fullName" autoComplete="name" minLength={2} maxLength={160} required /></label>
      <label>Work email<input type="email" name="email" autoComplete="email" maxLength={254} required /></label>
      <label>Company name<input name="companyName" autoComplete="organization" minLength={2} maxLength={160} required /></label>
      <label>Phone <small>optional</small><input type="tel" name="phone" autoComplete="tel" maxLength={40} /></label>
      <label>Primary trade<input name="trade" minLength={2} maxLength={100} required placeholder="Remodeling, flooring, roofing…" /></label>
      <label>Team size<select name="teamSize" defaultValue="2-5" required><option value="1">Just me</option><option value="2-5">2–5 people</option><option value="6-10">6–10 people</option><option value="11-25">11–25 people</option><option value="26+">26+ people</option></select></label>
      <label>Current software <small>optional</small><input name="currentSoftware" maxLength={120} placeholder="Jobber, spreadsheets, QuickBooks…" /></label>
      <label>How did you hear about us? <small>optional</small><input name="referralSource" maxLength={120} /></label>
    </div>
    <label className="beta-challenge">What is the biggest workflow problem you want Scopevora to solve?<textarea name="primaryChallenge" minLength={10} maxLength={2000} rows={5} required placeholder="Tell us where leads, estimates, scheduling, payments, or job information break down today." /></label>
    <label className="beta-consent"><input type="checkbox" name="consent" required /><span>I agree that Three Sisters Improvements, LLC may use this information to evaluate and contact me about the Scopevora beta. I have read the <a href="/privacy.html" target="_blank">beta privacy notice</a>.</span></label>
    {config?.turnstileSiteKey && <TurnstileWidget siteKey={config.turnstileSiteKey} action={config.turnstileAction} resetSignal={resetSignal} onToken={onToken} onError={onWidgetError} />}
    {configError && <p className="beta-form-error" role="alert">{configError}</p>}
    {notice && <p className={success ? "beta-form-success" : "beta-form-notice"} role="status">{success && <CheckCircle2 />}{notice}</p>}
    <div className="beta-form-footer">
      <button className="button copper" disabled={busy || secureFormLoading || Boolean(requiresChallenge && !token)}>{busy ? "Submitting…" : secureFormLoading ? "Preparing secure form…" : apiEnabled ? "Apply for private beta" : "Continue in email"}<ArrowRight size={17} /></button>
      <span><Mail size={14} /> {apiEnabled ? "We’ll contact you by email." : "This opens your email app; you still choose whether to send."}</span>
    </div>
  </form>;
}
